import React, { FunctionComponent } from 'react';
import { v4 as uuid } from 'uuid';
import { useDebouncedCallback } from 'use-debounce';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Translate } from 'react-localize-redux';
import { isValidUrl } from '@webapp/utils/validators';
import { parseLink } from '@webapp/sdk/tracpage';

// Components
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Button from '@components/actions/button';
import LoadingCtrl from '@webapp/controllers/loading';
import { TracPageLink } from '@services/tracpage/types/trac-page-link';
import { TracPageLinkType } from '@services/tracpage/types/trac-page-link-type';

const opacity = {
  visible: {
    opacity: 1,
    scaleY: 1,
    originY: 0,
    transition: {
      ease: [0.0, 0.0, 0.25, 1.0],
      duration: 0.5,
    },
  },
  hidden: {
    opacity: 0,
    scaleY: 0,
  },
};

interface NewLinkProps {
  className?: string;
  onAdd: (newLink: TracPageLink) => void;
  onCancel: () => void;
}

const NewLink: FunctionComponent<NewLinkProps> = ({
  className,
  onAdd,
  onCancel,
}) => {
  const [link, setLink] = React.useState<string>('');
  const [avatar, setAvatar] = React.useState<string | undefined>('');
  const [type, setType] = React.useState<TracPageLinkType>('url');
  const [title, setTitle] = React.useState<string>('');
  const [imgError, setImgError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const handleChangeLink = useDebouncedCallback((value: string) => {
    if (isValidUrl(value)) {
      setIsLoading(true);
      parseLink(value)
        .then((resp) => {
          const { title, avatar, type } = resp;
          setTitle(title);
          setAvatar(avatar);
          setType(type);
          setIsLoading(false);
        })
        .catch((error) => {
          // tslint:disable-next-line:no-console
          console.log('error: ', error); // eslint-disable-line no-console
          setIsLoading(false);
        });
    }
  }, 500);

  React.useEffect(() => {
    if (link) {
      handleChangeLink(link);
    }
  }, [link, handleChangeLink]);

  const handleAdd = () => {
    const newLink: TracPageLink = {
      id: uuid(),
      order: 0,
      type,
      url: link,
      title,
      avatar,
    };

    onAdd(newLink);
  };

  const leftDecoration = (
    <div>
      {!avatar || imgError ? (
        <SquareIcon />
      ) : (
        <Avatar src={avatar} alt={title} onError={() => setImgError(true)} />
      )}
    </div>
  );

  return (
    <Wrapper
      className={className || ''}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={opacity}
    >
      <InputField
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.NEW_LINK.LABEL" />
        }
        value={link}
        onChange={setLink}
        locked={isLoading}
      />
      <InputField
        name="title"
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.TITLE.LABEL" />
        }
        leftDecoration={leftDecoration}
        onChange={setTitle}
        autoComplete="off"
        value={title}
        locked={isLoading}
      />
      <Flex gap={5}>
        <Button buttonType="secondary" onClick={onCancel} fullWidth>
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.NEW_LINK.CANCEL" />
        </Button>
        <Button
          disabled={isLoading}
          buttonType="secondary-dark"
          onClick={handleAdd}
          fullWidth
        >
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.LINKS.INPUT.NEW_LINK.ADD" />
        </Button>
      </Flex>
      {isLoading && (
        <Loader size="48px" className="loading-animation is-loading" loading />
      )}
    </Wrapper>
  );
};

export default NewLink;

const Wrapper = styled(motion.div)`
  display: grid;
  grid-gap: 10px;
  position: relative;
`;

const SquareIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const Loader = styled(LoadingCtrl)`
  position: absolute;
  top: 0;
  right: 0;
`;
